import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Loader, Phone } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppForm } from "@/hooks/useCreateForm";

export const Route = createFileRoute(
  "/_authed/_org/settings/change-phone/confirm",
)({
  component: RouteComponent,
});

const formValidator = z.object({
  code: z.string().length(6, "Verification code must be 6 digits"),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { trpc, queryClient } = Route.useRouteContext();

  const { mutate: confirmPhoneChange, isPending: confirmSubmitting } =
    useMutation(
      trpc.auth.changePhone.mutationOptions({
        onError: (_error) => {
          toast.error("Failed to change phone number");
        },
        onSuccess: () => {
          toast.success("Phone number changed successfully");
          queryClient.invalidateQueries(trpc.auth.session.queryOptions());
          navigate({ to: "/settings" });
        },
      }),
    );

  const form = useAppForm({
    defaultValues: {
      code: "",
    },
    onSubmit: ({ value }) => {
      confirmPhoneChange({ challengeCode: value.code });
    },
    validators: {
      onSubmit: formValidator,
    },
  });

  return (
    <div className="p-4">
      <Button
        className="mb-4 inline-flex items-center"
        onClick={() => navigate({ to: "/settings" })}
        variant="ghost"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Settings
      </Button>

      <Card className="mx-auto max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            <CardTitle>Verify New Phone Number</CardTitle>
          </div>
          <CardDescription>
            Enter the verification code sent to your new phone number
          </CardDescription>
        </CardHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <CardContent className="space-y-4">
            <form.AppField
              children={(field) => {
                return (
                  <field.OTPField
                    description="Enter the 6-digit code sent to your new phone number"
                    label="Verification Code"
                    required={true}
                  />
                );
              }}
              name="code"
            />
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button
              className="w-full"
              disabled={confirmSubmitting}
              type="submit"
            >
              {confirmSubmitting ? "Verifying..." : "Verify Phone Number"}
              {confirmSubmitting ? (
                <Loader className="ml-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="ml-2 h-4 w-4" />
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
